import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@hooks/useTheme';
import { FloatingChatPanel } from './FloatingChatPanel';
import './clippy.css';

const TIPS = [
  'Need help with coverage analysis?',
  'I can generate reports for you!',
  'Ask me about share of voice trends.',
  'Want to find CEO interviews?',
  'I can help track negative coverage.',
  'Try asking about Tier 1 placements!',
];

const TIP_INTERVAL_MS = 25000;
const TIP_DISPLAY_MS = 5000;

const LS_POS = 'pr-chat-fab-offset';
const LS_MIN = 'pr-chat-fab-minimized';
const MARGIN = 24;
const FAB_SIZE = 56;
const PANEL_W = 380;
const PANEL_H = 500;
const PANEL_GAP = 12;
const DRAG_THRESHOLD = 8;

type FabPos = { bottom: number; right: number };

function loadMinimized(): boolean {
  try {
    return localStorage.getItem(LS_MIN) === '1';
  } catch {
    return false;
  }
}

function saveMinimized(value: boolean): void {
  try {
    if (value) localStorage.setItem(LS_MIN, '1');
    else localStorage.removeItem(LS_MIN);
  } catch {
    /* ignore */
  }
}

function loadPos(): FabPos {
  try {
    const raw = localStorage.getItem(LS_POS);
    if (raw) {
      const p = JSON.parse(raw) as unknown;
      if (
        p &&
        typeof p === 'object' &&
        typeof (p as FabPos).bottom === 'number' &&
        typeof (p as FabPos).right === 'number'
      ) {
        return { bottom: (p as FabPos).bottom, right: (p as FabPos).right };
      }
    }
  } catch {
    /* ignore */
  }
  return { bottom: MARGIN, right: MARGIN };
}

function savePos(pos: FabPos) {
  try {
    localStorage.setItem(LS_POS, JSON.stringify(pos));
  } catch {
    /* ignore */
  }
}

function stackHeight(chatOpen: boolean, tipVisible: boolean): number {
  let h = FAB_SIZE;
  if (chatOpen) h += PANEL_H + PANEL_GAP;
  if (tipVisible) h += 72;
  return h;
}

function clampPos(pos: FabPos, chatOpen: boolean, tipVisible: boolean): FabPos {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const h = stackHeight(chatOpen, tipVisible);
  const stackW = Math.max(PANEL_W, FAB_SIZE);
  const maxBottom = Math.max(MARGIN, vh - h - MARGIN);
  const maxRight = Math.max(MARGIN, vw - stackW - MARGIN);
  return {
    bottom: Math.min(Math.max(MARGIN, pos.bottom), maxBottom),
    right: Math.min(Math.max(MARGIN, pos.right), maxRight),
  };
}

export function ClippyAssistant() {
  const { isDark } = useTheme();
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [currentTip, setCurrentTip] = useState<string | null>(null);
  const [tipHiding, setTipHiding] = useState(false);
  const [fabPos, setFabPos] = useState<FabPos>(() => clampPos(loadPos(), false, false));
  /**
   * "Minimized" mode: the floating FAB at the bottom-right is hidden
   * and replaced with a small glowing chat dot parked next to the
   * bell icon in the page header. Click the dot to bring the FAB
   * back into place with a flight animation. Persisted to
   * localStorage so the user's choice survives reloads — if they
   * dismissed the chat on the last visit, it stays dismissed.
   */
  const [chatMinimized, setChatMinimized] = useState<boolean>(loadMinimized);

  const tipIndexRef = useRef(0);
  const tipTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  /**
   * Whether this is the first render of the component. Used to suppress
   * the flight animation on initial page load — the icon should simply
   * appear at its rest position the first time, and only animate when
   * the user later toggles between FAB and minimized modes.
   */
  const initialMountRef = useRef<boolean>(true);
  useEffect(() => {
    initialMountRef.current = false;
  }, []);

  /**
   * The minimized chat dot is rendered via React Portal into the
   * ``#chat-minimized-slot`` span the AppLayout's ``Header`` exposes
   * (see ``components/layout/Header.tsx``). That keeps the dot inside
   * the header's icon row so it doesn't overlap the theme toggle the
   * way the old ``position: fixed; right: 80px`` placement did.
   *
   * ``useLayoutEffect`` synchronously after DOM commit means we resolve
   * the slot reference before the first browser paint — the dot lands
   * in the correct spot on initial mount even when ``chatMinimized``
   * starts true (rehydrated from localStorage). The slot is null on
   * routes/pages that don't render the AppLayout header (login etc.);
   * we fall back to inline rendering with the original fixed-position
   * styling so the user can still recover the chat.
   */
  const [chatDotSlot, setChatDotSlot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setChatDotSlot(document.getElementById('chat-minimized-slot'));
  }, []);

  const lastPosRef = useRef<FabPos>(fabPos);

  const dragRef = useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    startBottom: number;
    startRight: number;
    dragging: boolean;
  }>({
    pointerId: null,
    startX: 0,
    startY: 0,
    startBottom: 0,
    startRight: 0,
    dragging: false,
  });

  useEffect(() => {
    lastPosRef.current = fabPos;
  }, [fabPos]);

  const hideTip = useCallback(() => {
    setTipHiding(true);
    hideTimerRef.current = setTimeout(() => {
      setCurrentTip(null);
      setTipHiding(false);
    }, 200);
  }, []);

  useEffect(() => {
    if (chatOpen) {
      setCurrentTip(null);
      setTipHiding(false);
      return;
    }

    const showNextTip = () => {
      const tip = TIPS[tipIndexRef.current % TIPS.length];
      tipIndexRef.current += 1;
      setCurrentTip(tip);
      setTipHiding(false);

      hideTimerRef.current = setTimeout(hideTip, TIP_DISPLAY_MS);
    };

    tipTimerRef.current = setTimeout(showNextTip, TIP_INTERVAL_MS);

    return () => {
      clearTimeout(tipTimerRef.current);
      clearTimeout(hideTimerRef.current);
    };
  }, [chatOpen, currentTip, hideTip]);

  const tipVisible = Boolean(currentTip && !chatOpen);

  useEffect(() => {
    setFabPos((p) => clampPos(p, chatOpen, tipVisible));
  }, [chatOpen, tipVisible]);

  useEffect(() => {
    const onResize = () => {
      setFabPos((p) => clampPos(p, chatOpen, tipVisible));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [chatOpen, tipVisible]);

  const toggleChat = useCallback(() => {
    setChatOpen((prev) => !prev);
    setCurrentTip(null);
    setTipHiding(false);
  }, []);

  /**
   * Persist the minimized state to localStorage every time it changes
   * so the user's preference survives reloads. ``saveMinimized``
   * cleans up the key entirely when the value is ``false`` so the
   * storage doesn't accumulate stale entries.
   */
  useEffect(() => {
    saveMinimized(chatMinimized);
  }, [chatMinimized]);

  /**
   * "Send the chat to the bell" handler. Closes the open chat panel
   * (if any), hides the floating tip bubble, and parks the chat dot
   * next to the header bell with a flight animation. The dot keeps
   * a soft pulsing glow at its rest position so the user can spot
   * it after the flight ends.
   */
  const handleMinimize = useCallback(() => {
    setChatOpen(false);
    setCurrentTip(null);
    setTipHiding(false);
    setChatMinimized(true);
  }, []);

  /**
   * "Bring the chat back" handler. Restores the FAB to its
   * bottom-right (or last-dragged) position with a flight-down
   * animation. The chat panel stays closed so the user can decide
   * whether to open it; matches the existing toggle behaviour where
   * clicking the FAB opens the panel.
   */
  const handleRestore = useCallback(() => {
    setChatMinimized(false);
  }, []);

  const bubbleClass = [
    'clippy-speech-bubble',
    isDark && 'clippy-speech-bubble--dark',
    tipHiding && 'clippy-speech-bubble--hidden',
  ]
    .filter(Boolean)
    .join(' ');

  const onDragPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('.clippy-chat-header-actions')) return;
    const p = lastPosRef.current;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startBottom: p.bottom,
      startRight: p.right,
      dragging: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onDragPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const d = dragRef.current;
      if (d.pointerId !== e.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.dragging && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
        d.dragging = true;
      }
      if (!d.dragging) return;
      const next: FabPos = {
        bottom: d.startBottom - dy,
        right: d.startRight - dx,
      };
      const clamped = clampPos(next, chatOpen, tipVisible);
      lastPosRef.current = clamped;
      setFabPos(clamped);
    },
    [chatOpen, tipVisible],
  );

  const onDragPointerUpFab = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const d = dragRef.current;
      if (d.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (!d.dragging) {
        toggleChat();
      } else {
        savePos(lastPosRef.current);
      }
      dragRef.current = { ...d, pointerId: null, dragging: false };
    },
    [toggleChat],
  );

  const onDragPointerUpHeader = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (d.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (d.dragging) {
      savePos(lastPosRef.current);
    }
    dragRef.current = { ...d, pointerId: null, dragging: false };
  }, []);

  const onDragPointerCancel = useCallback((e: React.PointerEvent<HTMLElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    dragRef.current.pointerId = null;
    dragRef.current.dragging = false;
  }, []);

  const headerDragHandlers = {
    onPointerDown: onDragPointerDown,
    onPointerMove: onDragPointerMove,
    onPointerUp: onDragPointerUpHeader,
    onPointerCancel: onDragPointerCancel,
  };

  if (location.pathname === '/chat') return null;

  // Whether the inbound element should play the flight animation.
  // Suppressed on initial mount so the icon doesn't streak across the
  // viewport every page load; only plays on user-driven transitions
  // between FAB and minimized modes.
  const playFlightAnim = !initialMountRef.current;

  if (chatMinimized) {
    // Minimized mode: render a small glowing chat dot inside the
    // header's icon row (via ``chatDotSlot`` portal target) so it
    // claims its own slot between the session bar and the theme
    // toggle. Clicking restores the FAB to its last-known
    // bottom-right position. ``clippyFlyUp`` mocks a flight from the
    // FAB origin (bottom-right) up to the dot's rest spot — the
    // keyframe is purely transform-based so the animation still reads
    // correctly regardless of whether the dot is portaled into the
    // header or rendered inline as a fallback.
    const inHeaderSlot = chatDotSlot != null;
    const dot = (
      <button
        type="button"
        className={[
          'clippy-minimized-dot',
          inHeaderSlot && 'clippy-minimized-dot--inline',
          playFlightAnim && 'clippy-minimized-dot--flying',
          isDark && 'clippy-minimized-dot--dark',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={handleRestore}
        aria-label="Bring the chat assistant back to the bottom-right corner"
        title="Bring chat back"
      >
        <span className="clippy-minimized-dot-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
            <circle cx="8" cy="12" r="1" fill="currentColor" stroke="none" />
            <circle cx="16" cy="12" r="1" fill="currentColor" stroke="none" />
          </svg>
        </span>
        <span className="clippy-minimized-dot-glow" aria-hidden="true" />
      </button>
    );
    return inHeaderSlot ? createPortal(dot, chatDotSlot) : dot;
  }

  return (
    <div
      className={[
        'clippy-container',
        playFlightAnim && 'clippy-container--flying',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        bottom: fabPos.bottom,
        right: fabPos.right,
        top: 'auto',
        left: 'auto',
      }}
    >
      {chatOpen && (
        <FloatingChatPanel
          onClose={() => setChatOpen(false)}
          isDark={isDark}
          headerDragHandlers={headerDragHandlers}
        />
      )}

      {currentTip && !chatOpen && (
        <div className={bubbleClass} onClick={hideTip}>
          {currentTip}
        </div>
      )}

      <button
        type="button"
        className={`chat-fab${chatOpen ? ' chat-fab--active' : ''}`}
        onPointerDown={onDragPointerDown}
        onPointerMove={onDragPointerMove}
        onPointerUp={onDragPointerUpFab}
        onPointerCancel={onDragPointerCancel}
        aria-label={chatOpen ? 'Close chat assistant' : 'Open chat assistant (drag to move)'}
        title="Drag to move · Click to open"
      >
        <span className="chat-fab-icon">
          {chatOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
              <circle cx="8" cy="12" r="1" fill="currentColor" stroke="none" />
              <circle cx="16" cy="12" r="1" fill="currentColor" stroke="none" />
            </svg>
          )}
        </span>
        {!chatOpen && <span className="chat-fab-pulse" />}
      </button>

      {!chatOpen && (
        // Small close (X) button overlaid on the top-right corner of
        // the FAB. Clicking it sends the chat assistant up to the
        // header bell with a flight animation. Rendered only when the
        // chat panel is closed — the panel has its own close affordance
        // in its header. ``stopPropagation`` on pointer events
        // prevents the FAB's drag-detection from initiating when the
        // user clicks the close button.
        <button
          type="button"
          className="clippy-fab-close-btn"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={handleMinimize}
          aria-label="Send chat assistant to the header bell"
          title="Send to top — click the glowing icon next to the bell to bring it back"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}
